import { ActivitySummary, ShareAgreement, ShareAgreementsApi, SharerAthlete, Configuration } from 'oas/datasharing';
import { UseQueryResult, useQueries } from '@tanstack/react-query';
import { useApiQuery } from '../../use-api-query';
import { useApiConfig, useAuth } from 'auth';
import axios from 'axios';
import { API_NAMES } from '../../constants';

export const useGetShareAgreements = (options = {}): UseQueryResult<ShareAgreement[]> => {
    return useApiQuery(
        ['datasharing', 'share-agreements'],
        (apiConfig, axiosInstance) => new ShareAgreementsApi(apiConfig, '', axiosInstance).getShareAgreements(),
        options,
        'v6',
        API_NAMES.DATASHARING,
    );
};

export const useGetSharerAthleteDetails = (shareAgreementId: string, options = {}): UseQueryResult<SharerAthlete> => {
    return useApiQuery(
        ['datasharing', 'sharer-athlete-details', shareAgreementId],
        (apiConfig, axiosInstance) =>
            new ShareAgreementsApi(apiConfig, '', axiosInstance).getSharerAthlete(shareAgreementId),
        options,
        'v6',
        API_NAMES.DATASHARING,
    );
};

/**
 * Fetches activity summaries for multiple agreement IDs in parallel.
 * Returns a map of agreementId → ActivitySummary[].
 */
export const useGetActivitySummariesBatch = (
    agreementIds: string[],
    enabled: boolean,
): { summariesMap: Map<string, ActivitySummary[]>; isSettled: boolean } => {
    const { apiConfig, axiosInstance } = useApiConfig('v6', API_NAMES.DATASHARING);
    const auth = useAuth();

    const results = useQueries({
        queries: agreementIds.map((id) => ({
            queryKey: ['datasharing', 'activity-summaries', id],
            queryFn: () => {
                const source = axios.CancelToken.source();
                const requestConfig = new Configuration({ ...apiConfig, baseOptions: { cancelToken: source.token } });
                return new ShareAgreementsApi(requestConfig, '', axiosInstance)
                    .getActivitySummaries(id, 'new')
                    .then((response) => response?.data);
            },
            enabled: auth.isAuthenticated && enabled && agreementIds.length > 0,
            staleTime: 600000,
        })),
    });

    const map = new Map<string, ActivitySummary[]>();
    results.forEach((result, index) => {
        if (result.data) {
            map.set(agreementIds[index], result.data);
        }
    });

    const isSettled = results.length > 0 && results.every((r) => r.isSuccess || r.isError);

    return { summariesMap: map, isSettled };
};
